import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useQueryCacheResourceHelpers } from '../../resources/utils/useQueryCacheResourceHelpers.js';
import { useMutation } from '@tanstack/react-query';
import { useWoody } from '../../woody/woody.js';
import { toast } from '../../store/alerts.js';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { getErrorDetail } from '../../errors/errors.utils.js';

const MAX_TITLE_LENGTH = 500;
const useMutationResourceTitleRename = (resourceId) => {
  const { client } = useWoody();
  const { updateCachedResource } = useQueryCacheResourceHelpers();
  const { track } = useAnalytics();
  const accessToken = useContextAccessToken();
  const mutation = useMutation({
    mutationKey: [
      "resource-rename",
      resourceId
    ],
    mutationFn: async ({ resource, newTitle }) => {
      if (newTitle.length > MAX_TITLE_LENGTH) {
        throw new Error("TOO_LONG");
      }
      if (resource.name === newTitle) {
        return Promise.resolve();
      }
      return await client.v2({
        endpoint: "/v2/resources/{resourceId}",
        params: {
          resourceId: resource.id
        }
      }, {
        method: "patch",
        body: {
          name: newTitle
        },
        query: {
          accessToken
        }
      });
    },
    onMutate: ({ resource, newTitle }) => {
      return updateCachedResource({
        id: resource.id,
        name: newTitle
      });
    },
    onError: (error, _v, context) => {
      const errorDetail = getErrorDetail(error);
      if (errorDetail === "err_resource_name_already_exists") {
        toast({
          id: "err_resource_name_already_exists",
          replace: true,
          content: "Item with this name already exists in this location."
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : "Failed to rename item";
        switch (errorMessage) {
          case "TOO_LONG": {
            toast({
              id: "TOO_LONG",
              replace: true,
              content: `Title is too long. Please use ${MAX_TITLE_LENGTH} characters or less.`
            });
            break;
          }
          default: {
            toast({
              id: "FAILED_TO_RENAME",
              replace: true,
              content: "Failed to rename item"
            });
          }
        }
      }
      context?.resetCacheToPreOptimisticState();
      context?.invalidateQueries();
    },
    onSuccess: (_, { resource }) => {
      track(AnalyticsEvents.RenamedResource, {
        resourceId: resource.id,
        type: resource.kind
      });
    }
  });
  return mutation;
};

export { MAX_TITLE_LENGTH, useMutationResourceTitleRename };
//# sourceMappingURL=useMutationResourceTitleRename.js.map
